/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import {
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants.js';

export const PointerMethods = {
  _getWorldPointerPosition() {
    if (!this._stage) return null;
    const pos = this._stage.getPointerPosition();
    if (!pos) return null;
    return {
      x: (pos.x - this._stage.x()) / this._stage.scaleX(),
      y: (pos.y - this._stage.y()) / this._stage.scaleY()
    };
  },

  _onPointerDown(event) {
    if (this._gridCalibration) {
      if ((event?.evt?.button ?? 0) !== 0) return;
      this._gridCalibrationPointerDown = true;
      return this._previewGridCalibration();
    }
    if (this._characterMove) return this._onCharacterMoveDown();
    if (this._tool === 'character') {
      this._placingCharacter = true;
      return this._previewCharacterToken();
    }
    if (this._tool === 'area-effect') {
      if (this._mobileEditor || this._usingTouch) return this._previewAreaEffect();
      return this._placeAreaEffect();
    }
    if (this._tool === 'position-manager') return this._onPositionManagerDown();
    if (this._tool === 'select') return this._onSelectDown(event);
    if (this._tool === 'drag') return this._onDragDown();
    if (this._tool === 'draw-rect' || this._tool === 'erase-rect' || this._tool === 'shape-rect')
      return this._onRectDown();

    // Pencil tools (draw / erase)
    if (this._tool === 'none' || !this._fogLayer) return;
    this._painting = true;
    this._lastLine = null;
    this._addFogPoint();
  },

  _onPointerMove() {
    if (this._gridCalibration) return this._previewGridCalibration();
    if (this._characterMove) return this._onCharacterMovePointer();
    if (this._tool === 'character') return this._previewCharacterToken();
    if (this._tool === 'area-effect') return this._previewAreaEffect();
    if (this._effectRotating) return this._rotateEffectAtPointer();
    if (this._tool === 'position-manager' && this._positionDragging) return this._onPositionManagerMove();
    if (this._tool === 'select' && this._shapeDragging) return this._onSelectMove();
    if (this._tool === 'select') this._updateEffectRotationCursor();
    if (this._tool === 'drag' && this._dragging) return this._onDragMove();

    if (this._painting && (this._tool === 'draw-rect' || this._tool === 'erase-rect' || this._tool === 'shape-rect')) {
      const worldPos = this._getWorldPointerPosition();
      if (worldPos) this._updateRectPreview(worldPos);
      return;
    }

    // Fog pencil/eraser cursor follows pointer
    if (this._cursorCircle && (this._tool === 'draw' || this._tool === 'erase')) {
      const worldPos = this._getWorldPointerPosition();
      if (worldPos) {
        this._cursorCircle.x(worldPos.x);
        this._cursorCircle.y(worldPos.y);
        this._cursorCircle.visible(true);
        this._cursorLayer.batchDraw();
      }
    }

    if (!this._painting) return;
    this._addFogPoint();
  },

  _onPointerUp() {
    if (this._gridCalibration) return this._placeGridCalibration();
    if (this._characterMove) return this._onCharacterMoveUp();
    if (this._tool === 'character') {
      if (this._placingCharacter) this._placeCharacterToken();
      this._placingCharacter = false;
      return;
    }
    if (this._effectRotating) return this._finishEffectRotation();
    if (this._tool === 'position-manager') return this._onPositionManagerUp();
    if (this._tool === 'select') return this._onSelectUp();
    if (this._tool === 'drag') {
      this._dragging = false;
      if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'grab';
      return;
    }

    if (this._painting && this._tool === 'shape-rect') {
      this._commitShapeRect();
      this._painting = false;
      return;
    }
    if (this._painting && (this._tool === 'draw-rect' || this._tool === 'erase-rect')) {
      this._commitFogRect();
      this._painting = false;
      this._save();
      return;
    }

    // Pencil stroke end
    if (!this._painting) return;
    this._painting = false;
    this._lastLine = null;
    this._save();
  },

  _onPointerLeave() {
    this._gridCalibrationPointerDown = false;
    if (this._touchGesture) return;
    this._characterMovePointer = null;
    this._placingCharacter = false;
    this._finishEffectRotation();
    if (this._areaEffectPreview) this._areaEffectPreview.visible(false);
    if (this._characterPreview) this._characterPreview.visible(false);
    if (this._tool === 'position-manager') {
      this._onPositionManagerUp();
      return;
    }

    if (this._cursorCircle) this._cursorCircle.visible(false);

    // Cancel in-progress rect preview
    if (this._rectPreviewShape) {
      this._rectPreviewShape.destroy();
      this._rectPreviewShape = null;
      this._rectStart = null;
      this._painting = false;
    }

    // Commit shape drag
    if (this._shapeDragging) {
      this._shapeDragging = false;
      if (this._shapeMoved && this._selectedShapeId) this._commitShapeMove();
      this._shapeMoved = false;
    }
    this._cycleShapeOnClick = false;

    if (this._cursorLayer) this._cursorLayer.batchDraw();
    this._dragging = false;
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'crosshair';
    this._gestureSnapshot = null;
    this._rememberEditorState?.();
  },

  _onWheel(event) {
    if (!this._stage) return;
    event.preventDefault();
    if (this.readonlyValue) return;

    if (this._positionManagerInteractionActive()) {
      this.positionZoomValue = Math.max(-95, Math.min(900, this.positionZoomValue + (event.deltaY < 0 ? POSITION_ZOOM_STEP : -POSITION_ZOOM_STEP)));
      this._savePosition({ debounce: true });
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      // Wheel ticks can arrive as several small events (especially touchpads).
      // Scale by distance instead of compounding a full zoom step per event.
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this._stage.height() : 1;
      const delta = Math.max(-100, Math.min(100, event.deltaY * unit));
      const nextScale = this._currentScale * Math.exp(-delta * 0.0015);
      const viewport = this.hasViewportTarget ? this.viewportTarget : this._stage.container();
      const bounds = viewport.getBoundingClientRect();

      this._applyZoom(nextScale, {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top
      });
      return;
    }

    const horizontalDelta = event.shiftKey ? event.deltaY || event.deltaX : event.deltaX;
    const verticalDelta = event.shiftKey ? 0 : event.deltaY;

    this._stage.x(this._stage.x() - horizontalDelta);
    this._stage.y(this._stage.y() - verticalDelta);
    this._stage.batchDraw();
    this._rememberEditorState?.();
  },

  _positionManagerInteractionActive() {
    return this._tool === 'position-manager' && this._positionManagerActive;
  },

  _onSelectDown(event) {
    this._cycleShapeOnClick = false;
    const worldPos = this._getWorldPointerPosition();
    if (!worldPos) return;
    if (this._startEffectRotation(worldPos)) return;
    if (this._isTransformerHandleTarget(event?.target)) return;
    const hit = this._findShapeAtPoint(worldPos);
    if (hit) {
      this._finishShapeAnimations?.();
      this._cycleShapeOnClick = hit.id === this._selectedShapeId;
      this._selectShape(hit.id, { showControls: !this._usingTouch });
      this._shapeDragStart = { x: worldPos.x, y: worldPos.y };
      this._shapeDragOrigPos = { x: hit.konvaShape.x(), y: hit.konvaShape.y() };
      this._shapeDragging = true;
      this._shapeMoved = false;
      if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'move';
    } else {
      this._deselectShape();
    }
  },

  _onSelectMove() {
    if (!this._selectedShapeId) return;
    const worldPos = this._getWorldPointerPosition();
    if (!worldPos) return;
    const dx = worldPos.x - this._shapeDragStart.x;
    const dy = worldPos.y - this._shapeDragStart.y;
    if (!this._shapeMoved && Math.hypot(dx, dy) * this._stage.scaleX() > (this._usingTouch ? 6 : 2))
      this._shapeMoved = true;
    if (!this._shapeMoved) return;

    const shape = this._findShapeById(this._selectedShapeId);
    if (!shape) return;
    const position = { x: this._shapeDragOrigPos.x + dx, y: this._shapeDragOrigPos.y + dy };
    if (shape.type === 'area-effect') this._moveAreaEffect(shape, position);
    else if (shape.type === 'character') this._moveCharacterToken(shape, position);
    else shape.konvaShape.position(position);
    if (['area-effect', 'character'].includes(shape.type)) {
      this._recreateLabels(shape);
    } else if (shape.konvaLabels) {
      shape.konvaLabels.x(shape.konvaShape.x() - this._shapeDragOrigPos.x);
      shape.konvaLabels.y(shape.konvaShape.y() - this._shapeDragOrigPos.y);
    }
    this._shapesLayerAbove.batchDraw();
  },

  _onSelectUp() {
    if (this._shapeDragging) {
      this._shapeDragging = false;
      if (this._shapeMoved && this._selectedShapeId) this._commitShapeMove();
      else if (this._cycleShapeOnClick) {
        const hit = this._findShapeAtPoint(this._shapeDragStart, { cycle: true });
        if (hit) this._selectShape(hit.id);
      } else if (this._usingTouch && this._selectedShapeId) this._selectShape(this._selectedShapeId);
      this._shapeMoved = false;
      this._cycleShapeOnClick = false;
      if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'default';
    }
  },

  _onDragDown() {
    const pos = this._stage.getPointerPosition();
    if (!pos) return;
    this._dragging = true;
    this._dragStart = { x: pos.x, y: pos.y };
    this._dragStartStagePos = { x: this._stage.x(), y: this._stage.y() };
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'grabbing';
  },

  _onDragMove() {
    if (!this._dragStart) return;
    const pos = this._stage.getPointerPosition();
    if (!pos) return;
    this._stage.x(this._dragStartStagePos.x + pos.x - this._dragStart.x);
    this._stage.y(this._dragStartStagePos.y + pos.y - this._dragStart.y);
    this._stage.batchDraw();
  }
};
